import React, { useEffect, useState } from "react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { Check, Loader2, Crown } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import api from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";

const PERKS = [
  "All 3 AI analyses + the model arena winner",
  "Competitor matrix with sources",
  "Live pricing research & sensitivity",
  "Full 90-day action plan",
  "Brand kit (palette, font, tagline)",
];

export default function UpgradeDialog({ open, onOpenChange }) {
  const { checkAuth } = useAuth();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get("/paypal/config");
        setConfig(res.data);
      } catch (e) {
        setConfig({ configured: false });
      } finally {
        setLoading(false);
      }
    })();
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="upgrade-dialog" className="bg-slate-900 border-slate-700 text-slate-100">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Crown className="h-5 w-5 text-orange-400" /> Go Premium</DialogTitle>
          <DialogDescription className="text-slate-400">One-time upgrade{config?.price ? ` — $${config.price}` : ""}. Unlock everything below.</DialogDescription>
        </DialogHeader>

        <ul className="space-y-2 my-2">
          {PERKS.map((p) => (
            <li key={p} className="flex items-start gap-2 text-sm text-slate-200">
              <Check className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" /> {p}
            </li>
          ))}
        </ul>

        {loading ? (
          <div className="flex justify-center py-6"><Loader2 className="h-6 w-6 animate-spin text-orange-400" /></div>
        ) : !config?.configured ? (
          <div data-testid="paypal-not-configured" className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-200">
            PayPal isn't configured yet. Add your PayPal sandbox Client ID & Secret to enable checkout.
          </div>
        ) : (
          <div className="mt-2">
            {paying && <div className="flex items-center gap-2 text-sm text-slate-400 mb-2"><Loader2 className="h-4 w-4 animate-spin" /> Finalising…</div>}
            <PayPalScriptProvider options={{ clientId: config.client_id, currency: "USD" }}>
              <PayPalButtons
                style={{ layout: "vertical", color: "gold", shape: "pill" }}
                createOrder={async () => {
                  const res = await api.post("/paypal/create-order");
                  return res.data.id;
                }}
                onApprove={async (data) => {
                  setPaying(true);
                  try {
                    await api.post("/paypal/capture-order", { order_id: data.orderID });
                    await checkAuth();
                    toast.success("You're premium! 🎉");
                    onOpenChange(false);
                  } catch (e) {
                    toast.error("Payment could not be completed");
                  } finally {
                    setPaying(false);
                  }
                }}
                onError={() => toast.error("PayPal error — please try again")}
              />
            </PayPalScriptProvider>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
